// TMOS Content Management Page
// Date: January 27, 2025
// Purpose: Admin UI for managing public website content

import React, { useState } from 'react';
import { 
  DocumentTextIcon,
  LinkIcon,
  CubeIcon,
  LightBulbIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

// Import tab components (to be created)
import { PrivacyPolicyTab } from '@/components/admin/content/PrivacyPolicyTab';
import { TermsOfServiceTab } from '@/components/admin/content/TermsOfServiceTab';
import { SocialLinksTab } from '@/components/admin/content/SocialLinksTab';
import { ProductContentTab } from '@/components/admin/content/ProductContentTab';
import { SolutionsTab } from '@/components/admin/content/SolutionsTab';
import { WhyTMOSTab } from '@/components/admin/content/WhyTMOSTab';
import { HomepageContentTab } from '@/components/admin/content/HomepageContentTab';
import { NavigationItemsTab } from '@/components/admin/content/NavigationItemsTab';
import { Bars3Icon } from '@heroicons/react/24/outline';

type TabType = 'privacy' | 'terms' | 'social' | 'product' | 'solutions' | 'why-tmos' | 'homepage' | 'navigation';

export const ContentManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('homepage');

  const tabs: Array<{ id: TabType; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'homepage', label: 'Homepage', icon: GlobeAltIcon },
    { id: 'navigation', label: 'Navigation', icon: Bars3Icon },
    { id: 'why-tmos', label: 'Why T-MOS', icon: GlobeAltIcon },
    { id: 'product', label: 'Product Content', icon: CubeIcon },
    { id: 'solutions', label: 'Solutions', icon: LightBulbIcon },
    { id: 'privacy', label: 'Privacy Policy', icon: DocumentTextIcon },
    { id: 'terms', label: 'Terms of Service', icon: DocumentTextIcon },
    { id: 'social', label: 'Social Links', icon: LinkIcon },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'homepage':
        return <HomepageContentTab />;
      case 'navigation':
        return <NavigationItemsTab />;
      case 'why-tmos':
        return <WhyTMOSTab />;
      case 'product':
        return <ProductContentTab />;
      case 'solutions':
        return <SolutionsTab />;
      case 'privacy':
        return <PrivacyPolicyTab />;
      case 'terms':
        return <TermsOfServiceTab />;
      case 'social':
        return <SocialLinksTab />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-[var(--color-surface-text)]">
              Content Management
            </CardTitle>
            <p className="text-sm text-[var(--color-secondary-text)] mt-1">
              Manage content for the public website
            </p>
          </CardHeader>
          <CardContent>
            {/* Tab Navigation */}
            <div className="border-b border-[var(--color-border)] mb-6">
              <nav className="flex space-x-1 overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap
                        border-b-2 transition-colors
                        ${isActive
                          ? 'border-[var(--color-accent-bg)] text-[var(--color-surface-text)] font-semibold'
                          : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {renderTabContent()}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

